import React, { useCallback, useEffect, useState } from 'react';
import {
  Sparkles, Loader2, X, Check, AlertTriangle, Info, Trash2, Plus, KeyRound, Cpu,
  Lock, Zap,
} from 'lucide-react';
import aiConfigService from '../../services/aiConfigService';

// =============================================================================
// Settings › AI Configuration.
//
// Where the endpoint URL and the API key for an AI service are set, and where the
// models offered by the Insights model selector are maintained.
//
// THE API KEY IS WRITE-ONLY, exactly as the SSO client secret is. It is never
// returned by the API, so this screen cannot show it and does not try: it shows
// whether one is set and the last four characters, and offers to replace it. A
// key rendered into an input is a credential in a devtools network tab, a screen
// share and a support screenshot.
//
// A SAVE THAT OMITS THE KEY KEEPS IT. That is why there is a deliberate "Replace"
// step rather than an always-editable field — without it, ticking Active and
// saving would wipe the credential, and the failure would appear later as
// authentication errors from the provider rather than here.
//
// TWO LEVELS, and keeping them separate matters: a PROVIDER is where to send a
// question (URL + key), a MODEL is what to ask. One provider serves several
// models at very different prices and context sizes, and the person asking the
// question picks the model — so models are configuration, not code.
//
// THE MOST CONSEQUENTIAL CONTROL ON THIS SCREEN IS NOT THE KEY. It is "Let this
// provider see the results", which decides whether real budget figures are sent
// to a third party. Off by default; it is presented as a distinct decision with
// its own explanation rather than as one checkbox among several, and turning it
// on asks for confirmation. The lopsidedness is intentional: switching it ON
// should take a moment's thought, switching it OFF should be instant.
// =============================================================================

const EMPTY_PROVIDER = {
  provider_code: '', display_name: '', api_style: 'gemini', base_url: '',
  is_active: false, timeout_seconds: 60, max_output_tokens: 4096, notes: '',
  // OFF, and the default is the whole point: an administrator who has not
  // considered the question gets the behaviour where nothing leaves.
  send_results_to_model: false,
};

export default function AiConfigModal({ onClose }) {
  const [data, setData] = useState({ providers: [], models: [], presets: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');
  const [editing, setEditing] = useState(null);   // provider form, or null
  const [addingModelFor, setAddingModelFor] = useState(null);
  const [testing, setTesting] = useState(null);   // provider_code being tested
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await aiConfigService.get();
      if (res.success) setData(res.data);
      else setError(res.message || 'Could not load the AI configuration.');
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load the AI configuration.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const say = (msg) => { setFlash(msg); setTimeout(() => setFlash(''), 2600); };

  const removeProvider = async (code) => {
    // Deleting the provider is the ONLY way to clear a stored key — see the
    // header comment on why a blank save keeps it. So the confirmation says so
    // explicitly rather than asking a vague "are you sure".
    if (!window.confirm(
      `Remove ${code}?\n\nThis deletes its stored API key and every model under it. ` +
      `Anyone currently choosing one of those models in Insights will lose it.`
    )) return;
    try {
      const res = await aiConfigService.deleteProvider(code);
      if (res.success) { say(res.message); await load(); } else setError(res.message);
    } catch (e) { setError(e.response?.data?.message || e.message); }
  };

  // A failed test is the USEFUL result here, so it comes back as a normal
  // response rather than an exception, and is shown in place rather than as an
  // error banner that reads like the screen is broken.
  const runTest = async (code) => {
    setTesting(code); setTestResult(null); setError('');
    try {
      const res = await aiConfigService.testProvider(code);
      setTestResult({ code, ...res });
    } catch (e) {
      setTestResult({ code, success: false, message: e.response?.data?.message || e.message });
    } finally { setTesting(null); }
  };

  // Adding a model the provider itself just named is the fastest route out of a
  // 404, and it cannot be mistyped — the code comes from the provider's list, not
  // from a person retyping it.
  const adoptModel = async (providerCode, m) => {
    try {
      const res = await aiConfigService.saveModel({
        provider_code: providerCode,
        model_code: m.model_code,
        display_name: m.display_name || m.model_code,
        context_tokens: m.context_tokens || null,
        is_default: !data.models.some((x) => x.is_default),
        is_active: true,
        sort_order: data.models.filter((x) => x.provider_code === providerCode).length * 10,
      });
      if (res.success) { setTestResult(null); say(`Added ${m.model_code}.`); await load(); }
      else setError(res.message);
    } catch (e) { setError(e.response?.data?.message || e.message); }
  };

  const removeModel = async (providerCode, modelCode) => {
    try {
      const res = await aiConfigService.deleteModel(providerCode, modelCode);
      if (res.success) { say('Model removed.'); await load(); } else setError(res.message);
    } catch (e) { setError(e.response?.data?.message || e.message); }
  };

  // Applying a preset fills the form; it does not save. Nothing is written until
  // somebody has looked at the URL and pasted a key, because a preset's base URL
  // has a shelf life and a half-configured active provider fails at question time.
  const applyPreset = (preset) => {
    setEditing({
      ...EMPTY_PROVIDER,
      provider_code: preset.provider_code,
      display_name: preset.display_name,
      api_style: preset.api_style,
      base_url: preset.base_url,
      _preset: preset,
      _isNew: true,
    });
  };

  const modelsOf = (code) => data.models.filter((m) => m.provider_code === code);

  return (
    <Shell title="AI Configuration" onClose={onClose}>
      {loading ? (
        <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : (
        <>
          <Notice tone="info" icon={Info} title="An active provider here is called for real.">
            A provider that is active with a key appears in the Insights model selector and will be sent questions.
            Saving a key does not prove it works — use <strong>Test</strong> to check the endpoint, key and model code
            before someone discovers a problem by asking a question.
          </Notice>

          {error && <Notice tone="danger" icon={AlertTriangle} title="Something went wrong.">{error}</Notice>}
          {flash && <Notice tone="info" icon={Check} title={flash} />}

          {/* ------------- CONFIGURED PROVIDERS ------------- */}
          {data.providers.length === 0 ? (
            <p className="m-0 mb-5 text-sm text-slate-500 dark:text-slate-400">
              No AI service is configured yet. Start from one of the presets below, or add a provider by hand.
            </p>
          ) : (
            <div className="mb-6 space-y-3">
              {data.providers.map((p) => (
                <div key={p.provider_code} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
                  <div className="flex items-start gap-3 px-4 py-3">
                    <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${p.is_active
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400'
                      : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'}`}>
                      <Sparkles className="w-4 h-4" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="m-0 text-sm font-semibold text-slate-900 dark:text-slate-100">{p.display_name}</h3>
                        <code className="text-[10px] text-slate-400">{p.provider_code}</code>
                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-slate-100 dark:bg-slate-700 text-slate-500">
                          {p.api_style}
                        </span>
                        {p.is_active
                          ? <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400">Active</span>
                          : <span className="px-1.5 py-0.5 rounded text-[10px] bg-slate-100 text-slate-500 dark:bg-slate-700">Inactive</span>}
                      </div>
                      <p className="m-0 mt-0.5 text-[11px] text-slate-500 dark:text-slate-400 break-all">{p.base_url}</p>
                      <p className="m-0 mt-0.5 text-[11px] text-slate-400">
                        {p.key_set
                          ? <>API key stored{p.api_key_hint ? <> · ends <code>{p.api_key_hint}</code></> : null}</>
                          : <span className="text-amber-600 dark:text-amber-400">No API key — this provider cannot be switched on.</span>}
                        {' · '}timeout {p.timeout_seconds}s · max {p.max_output_tokens.toLocaleString()} output tokens
                      </p>
                      {/* Stated on the card, not buried in the edit form. Whether
                          a provider sees the figures is the thing somebody will
                          be asked about, and it should be readable at a glance. */}
                      <p className={`m-0 mt-1 inline-flex items-center gap-1 text-[11px] font-medium ${p.send_results_to_model
                        ? 'text-amber-700 dark:text-amber-400' : 'text-emerald-700 dark:text-emerald-400'}`}>
                        {p.send_results_to_model ? <AlertTriangle className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                        {p.send_results_to_model
                          ? 'Sees query results — real budget figures are sent to this provider'
                          : 'Never sees results — figures stay in Mantle'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => runTest(p.provider_code)} disabled={!p.key_set || testing === p.provider_code}
                        title="Send a two-word request to check the endpoint, key and model code"
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-xs font-medium text-slate-600 dark:text-slate-300 hover:border-slate-400 disabled:opacity-40">
                        {testing === p.provider_code ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                        Test
                      </button>
                      <button onClick={() => setEditing({ ...p, _isNew: false })}
                        className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-xs font-medium text-slate-600 dark:text-slate-300 hover:border-slate-400">
                        Edit
                      </button>
                      <button onClick={() => removeProvider(p.provider_code)} aria-label={`Remove ${p.provider_code}`}
                        className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-400 hover:text-red-600 hover:border-red-300">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {testResult?.code === p.provider_code && (
                    <div className={`px-4 py-2.5 border-t text-[11px] ${testResult.success
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300'
                      : 'border-red-200 bg-red-50 text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300'}`}>
                      <p className="m-0 font-bold">{testResult.success ? 'Reached it.' : 'Could not reach it.'}</p>
                      <p className="m-0 mt-0.5">{testResult.message}</p>

                      {testResult.success && testResult.data && (
                        <p className="m-0 mt-0.5 opacity-80">
                          Replied in {testResult.data.duration_ms}ms. {testResult.data.note}
                        </p>
                      )}

                      {/* What was actually attempted. Half of every "it does not
                          work" is really "it called a different URL than I
                          thought", and that is invisible without this. */}
                      {!testResult.success && (testResult.endpoint || testResult.attempted_model) && (
                        <p className="m-0 mt-1 opacity-80">
                          Tried <code>{testResult.attempted_model}</code>
                          {testResult.endpoint && <> at <code>{testResult.endpoint}</code></>}
                          {testResult.status && <> · HTTP {testResult.status}</>}
                        </p>
                      )}

                      {/* THE PROVIDER'S OWN WORDS. Nearly always the useful bit,
                          and nobody should need shell access to read it. */}
                      {!testResult.success && testResult.detail && (
                        <details className="mt-1">
                          <summary className="cursor-pointer opacity-80">What the provider said</summary>
                          <pre className="m-0 mt-1 p-2 rounded bg-slate-900/80 text-slate-100 text-[10px] overflow-x-auto whitespace-pre-wrap">
                            {testResult.detail}
                          </pre>
                        </details>
                      )}

                      {/* THE ANSWER TO A 404. Click one to adopt it. */}
                      {!testResult.success && testResult.available_models?.length > 0 && (
                        <div className="mt-2">
                          <p className="m-0 font-bold">
                            This key can reach {testResult.available_models.length} model
                            {testResult.available_models.length === 1 ? '' : 's'}. Click one to add it:
                          </p>
                          <div className="mt-1 flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                            {testResult.available_models.map((m) => (
                              <button
                                key={m.model_code}
                                type="button"
                                onClick={() => adoptModel(p.provider_code, m)}
                                title={m.context_tokens ? `${m.context_tokens.toLocaleString()} token context` : undefined}
                                className="px-2 py-1 rounded border border-current/30 text-[10px] font-mono hover:bg-white/40 dark:hover:bg-white/10"
                              >
                                {m.model_code}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Models under this provider */}
                  <div className="px-4 py-3 bg-slate-50 dark:bg-slate-900/40 border-t border-slate-100 dark:border-slate-700">
                    <div className="flex items-center justify-between mb-2">
                      <p className="m-0 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        Models offered in Insights
                      </p>
                      <button onClick={() => setAddingModelFor(p.provider_code)}
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-600 dark:text-slate-300 hover:underline">
                        <Plus className="w-3 h-3" /> Add model
                      </button>
                    </div>
                    {modelsOf(p.provider_code).length === 0 ? (
                      <p className="m-0 text-[11px] text-slate-400">
                        None yet — without a model this provider offers nothing to choose in Insights.
                        {p.provider_code && data.presets.find((x) => x.provider_code === p.provider_code)?.models?.length > 0
                          && ' Suggested codes are in the preset below.'}
                      </p>
                    ) : (
                      <ul className="m-0 p-0 list-none space-y-1">
                        {modelsOf(p.provider_code).map((m) => (
                          <li key={m.model_code} className="flex items-center gap-2 text-[11px]">
                            <Cpu className="w-3 h-3 text-slate-400 shrink-0" />
                            <span className="font-medium text-slate-700 dark:text-slate-200">{m.display_name}</span>
                            <code className="text-slate-400">{m.model_code}</code>
                            {m.is_default && <span className="px-1.5 rounded bg-slate-200 dark:bg-slate-700 text-[10px] text-slate-600 dark:text-slate-300">default</span>}
                            {!m.is_active && <span className="text-slate-400">(inactive)</span>}
                            {m.context_tokens && <span className="text-slate-400">{formatContext(m.context_tokens)} context</span>}
                            {m.input_price_per_1k != null && (
                              <span className="text-slate-400" title="Input / output price per 1,000 tokens">
                                {m.price_currency} {Number(m.input_price_per_1k)}/{Number(m.output_price_per_1k ?? 0)} per 1k
                              </span>
                            )}
                            <button onClick={() => removeModel(p.provider_code, m.model_code)}
                              aria-label={`Remove ${m.model_code}`}
                              className="ml-auto text-slate-400 hover:text-red-600">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ------------- PRESETS ------------- */}
          <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
            <p className="m-0 mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Add a provider</p>
            <p className="m-0 mb-3 text-[11px] text-slate-400">
              A starting point, not a supported list — providers rename and retire models faster than these presets are
              updated, so check the endpoint and the model codes against your provider's current documentation before
              relying on them. Every field stays editable.
            </p>
            <div className="flex flex-wrap gap-2">
              {data.presets.map((preset) => (
                <button key={preset.provider_code} onClick={() => applyPreset(preset)}
                  disabled={data.providers.some((p) => p.provider_code === preset.provider_code)}
                  className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-xs font-medium text-slate-600 dark:text-slate-300 hover:border-slate-400 disabled:opacity-40 disabled:cursor-not-allowed">
                  {preset.display_name}
                </button>
              ))}
              <button onClick={() => setEditing({ ...EMPTY_PROVIDER, _isNew: true })}
                className="px-3 py-2 rounded-lg border border-dashed border-slate-300 dark:border-slate-600 text-xs font-medium text-slate-500 hover:border-slate-400">
                Something else…
              </button>
            </div>
          </div>
        </>
      )}

      {editing && (
        <ProviderForm
          initial={editing}
          onCancel={() => setEditing(null)}
          onSaved={async (msg) => { setEditing(null); say(msg); await load(); }}
          onError={setError}
        />
      )}

      {addingModelFor && (
        <ModelForm
          providerCode={addingModelFor}
          preset={data.presets.find((p) => p.provider_code === addingModelFor)}
          existing={data.models.filter((m) => m.provider_code === addingModelFor)}
          hasDefault={data.models.some((m) => m.is_default)}
          onCancel={() => setAddingModelFor(null)}
          onSaved={async (msg) => { setAddingModelFor(null); say(msg); await load(); }}
          onError={setError}
        />
      )}
    </Shell>
  );
}

// -----------------------------------------------------------------------------
// Provider form. The API key handling here is the part that matters.
// -----------------------------------------------------------------------------
const ProviderForm = ({ initial, onCancel, onSaved, onError }) => {
  const [f, setF] = useState(initial);
  const [apiKey, setApiKey] = useState('');
  // A provider with no stored key starts in "replacing" mode because there is
  // nothing to keep; one that has a key requires an explicit choice to replace it.
  const [replacing, setReplacing] = useState(!initial.key_set);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState('');

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true); setProblem('');
    try {
      const payload = {
        provider_code: f.provider_code.trim().toUpperCase(),
        display_name: f.display_name,
        api_style: f.api_style,
        base_url: f.base_url,
        is_active: !!f.is_active,
        timeout_seconds: Number(f.timeout_seconds) || 60,
        max_output_tokens: Number(f.max_output_tokens) || 4096,
        notes: f.notes || '',
        // The server also refuses this on an inactive provider (a DB constraint
        // backs it); mirroring it here keeps the form honest rather than letting
        // a save silently drop it.
        send_results_to_model: !!f.send_results_to_model && !!f.is_active,
      };
      // Sent ONLY when deliberately replacing. Absent means "keep what is
      // stored" — never "clear it".
      if (replacing && apiKey.trim()) payload.api_key = apiKey.trim();

      const res = await aiConfigService.saveProvider(payload);
      if (res.success) onSaved('Provider saved.');
      else setProblem(res.message || 'Save failed.');
    } catch (e) {
      setProblem(e.response?.data?.message || e.message || 'Save failed.');
    } finally { setSaving(false); }
  };

  const isLocal = /^https?:\/\/(localhost|127\.0\.0\.1|::1|host\.docker\.internal)/i.test(f.base_url || '');

  return (
    <Overlay onCancel={onCancel}>
      <h3 className="m-0 mb-4 text-base font-semibold text-slate-900 dark:text-slate-100">
        {f._isNew ? 'Add an AI provider' : `Edit ${f.display_name}`}
      </h3>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Code" hint="Short identifier. Cannot be changed later.">
          <input value={f.provider_code} disabled={!f._isNew}
            onChange={(e) => set('provider_code', e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
            placeholder="GEMINI" className={`${INPUT} disabled:opacity-60`} />
        </Field>
        <Field label="Name" hint="What administrators and askers see.">
          <input value={f.display_name} onChange={(e) => set('display_name', e.target.value)}
            placeholder="Google Gemini" className={INPUT} />
        </Field>
      </div>

      <Field label="API style"
        hint="The wire format, not the brand — it decides how the request and the auth header are built. Azure OpenAI and a self-hosted OpenAI-compatible server are both 'openai'.">
        <select value={f.api_style} onChange={(e) => set('api_style', e.target.value)} className={INPUT}>
          <option value="gemini">Google Gemini</option>
          <option value="anthropic">Anthropic</option>
          <option value="openai">OpenAI-compatible</option>
        </select>
      </Field>

      <Field label="Endpoint URL" hint="The API base. No key and no model name — those are separate fields.">
        <input value={f.base_url} onChange={(e) => set('base_url', e.target.value)}
          placeholder="https://generativelanguage.googleapis.com/v1beta" className={INPUT} />
        {f.base_url && !/^https:/i.test(f.base_url) && !isLocal && (
          <p className="m-0 mt-1 text-[11px] text-red-600 dark:text-red-400">
            Use https for a remote endpoint — an API key sent over plain http can be read in transit.
          </p>
        )}
      </Field>

      <Field label="API key" hint="Encrypted before it is stored, and never sent back to this screen — not to anyone, including you.">
        {f.key_set && !replacing ? (
          <div className="flex items-center gap-2">
            <span className="flex-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 text-sm text-slate-500 dark:text-slate-400">
              ••••••••••••••••{f.api_key_hint ? f.api_key_hint : ''} <span className="text-[11px]">stored</span>
            </span>
            <button type="button" onClick={() => setReplacing(true)}
              className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-xs font-medium text-slate-600 dark:text-slate-300 hover:border-slate-400">
              Replace
            </button>
          </div>
        ) : (
          <>
            <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
              autoComplete="new-password" placeholder="Paste the key from your provider" className={INPUT} />
            {f._preset?.key_help && <p className="m-0 mt-1 text-[11px] text-slate-400">{f._preset.key_help}</p>}
            {f.key_set && (
              <button type="button" onClick={() => { setReplacing(false); setApiKey(''); }}
                className="mt-1 text-[11px] text-slate-500 hover:underline">
                Keep the existing key instead
              </button>
            )}
          </>
        )}
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Timeout (seconds)">
          <input type="number" min={5} max={600} value={f.timeout_seconds}
            onChange={(e) => set('timeout_seconds', e.target.value)} className={INPUT} />
        </Field>
        <Field label="Max output tokens">
          <input type="number" min={256} max={200000} value={f.max_output_tokens}
            onChange={(e) => set('max_output_tokens', e.target.value)} className={INPUT} />
        </Field>
      </div>

      <label className="flex items-start gap-2 mt-2 cursor-pointer">
        <input type="checkbox" className="mt-0.5" checked={!!f.is_active}
          onChange={(e) => {
            set('is_active', e.target.checked);
            // Switching a provider off also switches off result sharing. Leaving
            // a stale tick behind would mean re-activating silently resumes
            // sending figures, which nobody would expect.
            if (!e.target.checked) set('send_results_to_model', false);
          }} />
        <span>
          <span className="block text-sm font-medium text-slate-800 dark:text-slate-100">Active</span>
          <span className="block text-[11px] text-slate-500 dark:text-slate-400">
            Only active providers with a stored key appear in the Insights model selector. A provider cannot be made
            active without a key — a model that returns an authentication error looks like a broken feature rather than
            missing configuration.
          </span>
        </span>
      </label>

      {/* ---- THE DECISION THAT MATTERS. Given its own bordered block rather
           than being a checkbox in a list, because it is not the same kind of
           choice as a timeout. ---- */}
      <div className={`mt-5 rounded-lg border p-3 ${f.send_results_to_model
        ? 'border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10'
        : 'border-slate-200 dark:border-slate-700'}`}>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={!!f.send_results_to_model}
            disabled={!f.is_active}
            onChange={(e) => {
              // Turning it ON asks; turning it OFF is instant. Friction belongs
              // on the side that increases exposure, never on the side that
              // reduces it.
              if (!e.target.checked) return set('send_results_to_model', false);
              const okay = window.confirm([
                'Let this provider see query results?',
                '',
                'When someone asks a question, the figures the query returns - real budget, cost and revenue numbers for this tenant - will be sent to this provider so it can write an answer.',
                '',
                'With this off, the provider still chooses the query, but Mantle runs it and shows the table locally. Nothing leaves.',
                '',
                'Every question is recorded in ai_insight_log, including whether figures were sent.',
              ].join(String.fromCharCode(10)));
              if (okay) set('send_results_to_model', true);
            }}
          />
          <span>
            <span className="flex items-center gap-1.5 text-sm font-medium text-slate-800 dark:text-slate-100">
              {f.send_results_to_model
                ? <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                : <Lock className="w-3.5 h-3.5 text-slate-400" />}
              Let this provider see query results
            </span>
            <span className="block mt-1 text-[11px] text-slate-500 dark:text-slate-400">
              {f.send_results_to_model ? (
                <>
                  <strong>On.</strong> Budget figures returned by a query are sent to this provider so it can write a
                  plain-language answer. The question, the catalogue and now the results all leave your infrastructure.
                </>
              ) : (
                <>
                  <strong>Off.</strong> The provider is told what your data <em>means</em> and picks the query; Mantle runs
                  it and renders the table. No figures leave. People get the right numbers without a written interpretation.
                </>
              )}
            </span>
            {!f.is_active && (
              <span className="block mt-1 text-[11px] text-slate-400">
                Only available once the provider is active — an inactive provider is never called at all.
              </span>
            )}
            <span className="block mt-1 text-[11px] text-slate-400">
              A self-hosted model is the case where this can be on with nothing leaving your network.
            </span>
          </span>
        </label>
      </div>

      {problem && <Notice tone="danger" icon={AlertTriangle} title="Could not save.">{problem}</Notice>}

      <div className="mt-5 flex items-center justify-end gap-2">
        <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-sm font-medium text-slate-600 dark:text-slate-300 hover:border-slate-400">
          Cancel
        </button>
        <button onClick={save} disabled={saving}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-900 dark:bg-slate-600 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-40">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
        </button>
      </div>
    </Overlay>
  );
};

// -----------------------------------------------------------------------------
// Model form. Small, but the suggested-codes list saves real time and the
// context-window field is load-bearing: it governs how much catalogue is sent.
// -----------------------------------------------------------------------------
const ModelForm = ({ providerCode, preset, existing, hasDefault, onCancel, onSaved, onError }) => {
  const [m, setM] = useState({
    provider_code: providerCode, model_code: '', display_name: '',
    context_tokens: '', is_default: !hasDefault, is_active: true, sort_order: existing.length * 10,
    // Blank, not zero. Unpriced and free are different things, and a self-hosted
    // model is the first — see the migration.
    input_price_per_1k: '', output_price_per_1k: '', price_currency: 'USD',
  });
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState('');

  const set = (k, v) => setM((x) => ({ ...x, [k]: v }));

  const suggestions = (preset?.models || []).filter(
    (s) => !existing.some((e) => e.model_code === s.model_code)
  );

  const save = async () => {
    setSaving(true); setProblem('');
    try {
      const res = await aiConfigService.saveModel({
        ...m,
        context_tokens: m.context_tokens ? Number(m.context_tokens) : null,
        sort_order: Number(m.sort_order) || 0,
      });
      if (res.success) onSaved('Model saved.');
      else setProblem(res.message || 'Save failed.');
    } catch (e) {
      setProblem(e.response?.data?.message || e.message || 'Save failed.');
    } finally { setSaving(false); }
  };

  return (
    <Overlay onCancel={onCancel}>
      <h3 className="m-0 mb-1 text-base font-semibold text-slate-900 dark:text-slate-100">Add a model</h3>
      <p className="m-0 mb-4 text-[11px] text-slate-400">for {providerCode}</p>

      {suggestions.length > 0 && (
        <div className="mb-4">
          <p className="m-0 mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">Suggested</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button key={s.model_code} type="button"
                onClick={() => setM((x) => ({ ...x, model_code: s.model_code, display_name: s.display_name, context_tokens: s.context_tokens || '' }))}
                className="px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-600 text-xs text-slate-600 dark:text-slate-300 hover:border-slate-400">
                {s.display_name}
              </button>
            ))}
          </div>
          <p className="m-0 mt-1.5 text-[11px] text-slate-400">
            Check these against your provider's current model list — a retired code fails at question time.
          </p>
        </div>
      )}

      <Field label="Model code" hint="Exactly as the provider's API expects it. For Azure OpenAI, your deployment name.">
        <input value={m.model_code} onChange={(e) => set('model_code', e.target.value)}
          placeholder="gemini-2.5-pro" className={INPUT} />
      </Field>

      <Field label="Name" hint="What the person asking sees in the selector.">
        <input value={m.display_name} onChange={(e) => set('display_name', e.target.value)}
          placeholder="Gemini 2.5 Pro" className={INPUT} />
      </Field>

      <Field label="Context window (tokens)"
        hint="Optional, but used to decide how much of the data catalogue fits in the briefing. Left blank, the whole catalogue is sent and a small model may reject it.">
        <input type="number" min={1000} value={m.context_tokens}
          onChange={(e) => set('context_tokens', e.target.value)} placeholder="200000" className={INPUT} />
      </Field>

      {/* ---- PRICING ----
           Optional, and the screen says so. It exists because the Insights thread
           shows what each question cost, and it can only do that if somebody has
           told it the rate. Providers change these often enough that they belong
           in a row rather than in code. */}
      <div className="mb-4 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
        <p className="m-0 mb-1 text-sm font-medium text-slate-700 dark:text-slate-200">Pricing (optional)</p>
        <p className="m-0 mb-2.5 text-[11px] text-slate-500 dark:text-slate-400">
          Per 1,000 tokens, from your provider's pricing page. Fill these in and Insights shows what each question cost;
          leave them blank and it shows tokens only. A self-hosted model has no rate — leave them empty rather than
          entering zero, which would read as "free".
        </p>
        <div className="grid grid-cols-3 gap-2">
          <Field label="Input / 1k">
            <input type="number" step="0.0001" min="0" value={m.input_price_per_1k}
              onChange={(e) => set('input_price_per_1k', e.target.value)} placeholder="0.00125" className={INPUT} />
          </Field>
          <Field label="Output / 1k">
            <input type="number" step="0.0001" min="0" value={m.output_price_per_1k}
              onChange={(e) => set('output_price_per_1k', e.target.value)} placeholder="0.01" className={INPUT} />
          </Field>
          <Field label="Currency">
            <input value={m.price_currency}
              onChange={(e) => set('price_currency', e.target.value.toUpperCase().slice(0, 3))}
              placeholder="USD" className={INPUT} />
          </Field>
        </div>
      </div>

      <label className="flex items-center gap-2 mt-2 cursor-pointer">
        <input type="checkbox" checked={!!m.is_default} onChange={(e) => set('is_default', e.target.checked)} />
        <span className="text-sm text-slate-800 dark:text-slate-100">
          Pre-select this model in Insights
          <span className="block text-[11px] text-slate-500 dark:text-slate-400">
            Only one model can be the default; choosing this moves it off whichever model holds it now.
          </span>
        </span>
      </label>

      {problem && <Notice tone="danger" icon={AlertTriangle} title="Could not save.">{problem}</Notice>}

      <div className="mt-5 flex items-center justify-end gap-2">
        <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-sm font-medium text-slate-600 dark:text-slate-300 hover:border-slate-400">
          Cancel
        </button>
        <button onClick={save} disabled={saving || !m.model_code.trim() || !m.display_name.trim()}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-900 dark:bg-slate-600 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-40">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
        </button>
      </div>
    </Overlay>
  );
};

// -----------------------------------------------------------------------------
// Shared bits — same visual language as SsoConfigModal.
// -----------------------------------------------------------------------------
const Shell = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-6 font-sans" onMouseDown={onClose}>
    <div className="bg-white dark:bg-slate-800 w-full max-w-3xl rounded-xl shadow-2xl flex flex-col max-h-[88vh] overflow-hidden"
      onMouseDown={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 shrink-0">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-slate-500" />
          <h2 className="m-0 text-lg font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
        </div>
        <button onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">{children}</div>
      <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700 shrink-0 bg-slate-50 dark:bg-slate-800/60">
        <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-400">
          <KeyRound className="w-3 h-3" /> API keys are encrypted at rest and never returned by the API.
        </span>
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-sm font-medium text-slate-600 dark:text-slate-300 hover:border-slate-400">
          Close
        </button>
      </div>
    </div>
  </div>
);

// A second layer above the settings modal. z-index is one higher so the form sits
// over the list it was opened from rather than behind it.
const Overlay = ({ children, onCancel }) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-6" onMouseDown={onCancel}>
    <div className="bg-white dark:bg-slate-800 w-full max-w-xl rounded-xl shadow-2xl p-6 max-h-[88vh] overflow-y-auto"
      onMouseDown={(e) => e.stopPropagation()}>
      {children}
    </div>
  </div>
);

// A million-token window renders as "1049k" without this, which reads like a
// typo rather than a very large number.
const formatContext = (n) => (n >= 1000000 ? `${(n / 1000000).toFixed(n % 1000000 === 0 ? 0 : 1)}M` : `${Math.round(n / 1000)}k`);

const INPUT = 'w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-slate-900 dark:text-slate-100 outline-none focus:border-slate-800 dark:focus:border-slate-400';

const Field = ({ label, hint, children }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">{label}</label>
    {hint && <p className="m-0 mb-1.5 text-[11px] text-slate-500 dark:text-slate-400">{hint}</p>}
    {children}
  </div>
);

const TONES = {
  info: 'border-blue-300 bg-blue-50 text-blue-800 dark:border-blue-500/40 dark:bg-blue-500/10 dark:text-blue-300',
  warn: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300',
  danger: 'border-red-300 bg-red-50 text-red-800 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300',
};

const Notice = ({ tone, icon: Icon, title, children }) => (
  <div className={`mb-4 flex items-start gap-2 px-3 py-2.5 rounded-lg border ${TONES[tone]}`}>
    <Icon className="w-4 h-4 shrink-0 mt-0.5" />
    <div className="min-w-0">
      <p className="m-0 text-xs font-bold">{title}</p>
      {children && <p className="m-0 mt-1 text-[11px] opacity-90">{children}</p>}
    </div>
  </div>
);
